import { useState } from "react";
import {
  useCreateFlag,
  useDeleteOverride,
  useFlagOverrides,
  usePatchFlag,
  usePutOverride,
  RULE_ATTRIBUTES,
  type Flag,
  type Rule,
  type RuleAttribute,
} from "@/api/admin/flags";
import { useConfirmedChange } from "../confirmChange";
import type { Risk } from "../risk";
import { useAdminT } from "../useAdminT";
import {
  BTN,
  BTN_DISABLED,
  BTN_GHOST,
  CHECK_LABEL,
  COL,
  ERROR,
  FIELD,
  LABEL,
  LINK_BTN,
  ROW,
} from "./styles";

const PLATFORMS = ["telegram", "ios", "web"] as const;
const TIERS = ["free", "monthly", "annual"] as const;
const LOCALES = ["ru", "en", "es", "pt"] as const;
const ATTRIBUTE_OPTIONS: Record<RuleAttribute, readonly string[]> = {
  platform: PLATFORMS,
  subscription_tier: TIERS,
  locale: LOCALES,
};

interface Props {
  settingKey: string;
  name: string;
  risk: Risk;
  /** Feature flag row; absent when the flag was never created. */
  flag?: Flag;
}

/** Feature flag: global switch, segment rules, per-user overrides; or Create when the flag does not exist. */
export function FlagEditor({ settingKey, name, risk, flag }: Props): JSX.Element {
  const t = useAdminT();
  const create = useCreateFlag();
  const patch = usePatchFlag();
  const { confirmChange, modal } = useConfirmedChange();
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!flag) {
    return (
      <div style={COL}>
        <div style={ROW}>
          <span style={LABEL}>{t("shell.flag_not_created")}</span>
          <button
            style={create.isPending ? BTN_DISABLED : BTN}
            disabled={create.isPending}
            onClick={() =>
              confirmChange({ name, risk }, "no flag", settingKey, () =>
                create.mutate(
                  { key: settingKey },
                  { onError: (err) => setError((err as Error).message) }
                )
              )
            }
          >
            {t("shell.create")}
          </button>
        </div>
        {error && <div style={ERROR}>{error}</div>}
        {modal}
      </div>
    );
  }

  const onOff = (v: boolean): string => (v ? "On" : "Off");
  return (
    <div style={COL}>
      <div style={ROW}>
        <span style={LABEL}>{t("flags.overrides_count", { count: flag.overrides_count })}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={CHECK_LABEL}>
            <span style={LABEL}>{t("flags.global")}</span>
            <input
              aria-label={`${settingKey} ${t("flags.global")}`}
              type="checkbox"
              checked={flag.enabled}
              onChange={() =>
                confirmChange({ name, risk }, onOff(flag.enabled), onOff(!flag.enabled), () =>
                  patch.mutate({ key: flag.key, enabled: !flag.enabled })
                )
              }
            />
          </label>
          <button style={LINK_BTN} onClick={() => setExpanded(!expanded)}>
            {expanded ? t("flags.collapse") : t("flags.expand")}
          </button>
        </div>
      </div>
      {expanded && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 8,
            borderTop: "1px solid var(--d-line)",
          }}
        >
          <RuleEditor flag={flag} name={name} risk={risk} />
          <OverridesPanel flagKey={flag.key} />
        </div>
      )}
      {modal}
    </div>
  );
}

function emptyRule(): Rule {
  return { attribute: "platform", values: [], enabled: true };
}

function RuleEditor({ flag, name, risk }: { flag: Flag; name: string; risk: Risk }): JSX.Element {
  const t = useAdminT();
  const patch = usePatchFlag();
  const { confirmChange, modal } = useConfirmedChange();
  const [rules, setRules] = useState<Rule[]>(flag.rules);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(rules) !== JSON.stringify(flag.rules);
  // Backend rejects a rule with no selected `values` (RuleIn.values has
  // min_length=1) — block Save client-side instead of round-tripping a 422.
  const hasEmptyValues = rules.some((r) => r.values.length === 0);
  const hasBadPercent = rules.some(
    (r) =>
      r.rollout_percent != null &&
      (!Number.isInteger(r.rollout_percent) || r.rollout_percent < 0 || r.rollout_percent > 100)
  );
  const canSave = dirty && !hasEmptyValues && !hasBadPercent;

  function updateRule(i: number, fragment: Partial<Rule>): void {
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...fragment } : r)));
  }
  function setPercent(i: number, raw: string): void {
    setRules((rs) =>
      rs.map((r, idx) => {
        if (idx !== i) return r;
        // Empty field removes the key entirely (absent == 100%).
        if (raw === "") {
          const { rollout_percent: _dropped, ...rest } = r;
          return rest;
        }
        return { ...r, rollout_percent: Number(raw) };
      })
    );
  }
  function toggleValue(i: number, value: string): void {
    setRules((rs) =>
      rs.map((r, idx) => {
        if (idx !== i) return r;
        const values = r.values.includes(value)
          ? r.values.filter((v) => v !== value)
          : [...r.values, value];
        return { ...r, values };
      })
    );
  }
  const summary = (rs: Rule[]): string =>
    rs.length === 0
      ? "No rules"
      : rs
          .map(
            (r) =>
              `${r.attribute}: ${r.values.join(", ")}${r.rollout_percent != null ? ` ${r.rollout_percent}%` : ""}${r.enabled ? "" : " (off)"}`
          )
          .join("; ");

  return (
    <div style={COL}>
      <div style={{ ...LABEL, fontWeight: 600, fontSize: 13 }}>{t("flags.rules")}</div>
      {rules.map((rule, i) => (
        // Rules have no stable id in this draft list — index-as-key is fine here.
        <div
          key={i}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 10,
            borderRadius: 10,
            border: "1px solid var(--d-line)",
          }}
        >
          <select
            aria-label={`rule-${i}-attribute`}
            value={rule.attribute}
            onChange={(e) =>
              updateRule(i, { attribute: e.target.value as RuleAttribute, values: [] })
            }
          >
            {RULE_ATTRIBUTES.map((attr) => (
              <option key={attr} value={attr}>
                {t(`flags.attribute.${attr}`)}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ATTRIBUTE_OPTIONS[rule.attribute].map((opt) => (
              <label key={opt} style={CHECK_LABEL}>
                <input
                  type="checkbox"
                  aria-label={`rule-${i}-${opt}`}
                  checked={rule.values.includes(opt)}
                  onChange={() => toggleValue(i, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
          <label style={CHECK_LABEL}>
            {t("flags.rule_rollout_percent")}
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              aria-label={`rule-${i}-rollout-percent`}
              style={FIELD}
              value={rule.rollout_percent ?? ""}
              onChange={(e) => setPercent(i, e.target.value)}
            />
          </label>
          <label style={CHECK_LABEL}>
            <input
              type="checkbox"
              aria-label={`rule-${i}-enabled`}
              checked={rule.enabled}
              onChange={(e) => updateRule(i, { enabled: e.target.checked })}
            />
            {t("flags.rule_enabled")}
          </label>
          <div>
            <button
              style={LINK_BTN}
              onClick={() => setRules((rs) => rs.filter((_, idx) => idx !== i))}
            >
              {t("common.remove")}
            </button>
          </div>
        </div>
      ))}
      <div style={LABEL}>{t("flags.rule_rollout_hint")}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={BTN_GHOST} onClick={() => setRules((rs) => [...rs, emptyRule()])}>
          {t("flags.add_rule")}
        </button>
        <button
          style={canSave ? BTN : BTN_DISABLED}
          disabled={!canSave}
          onClick={() =>
            confirmChange({ name, risk }, summary(flag.rules), summary(rules), () =>
              patch.mutate(
                { key: flag.key, rules },
                {
                  onSuccess: () => setError(null),
                  onError: (err) => setError((err as Error).message),
                }
              )
            )
          }
        >
          {t("common.save")}
        </button>
      </div>
      {hasEmptyValues && <div style={LABEL}>{t("flags.rule_values_required")}</div>}
      {error && <div style={ERROR}>{error}</div>}
      {modal}
    </div>
  );
}

function OverridesPanel({ flagKey }: { flagKey: string }): JSX.Element {
  const t = useAdminT();
  const { data, isLoading } = useFlagOverrides(flagKey);
  const put = usePutOverride(flagKey);
  const del = useDeleteOverride(flagKey);
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);

  function addOverride(): void {
    const parsed = Number(userId);
    if (!userId || Number.isNaN(parsed)) {
      setError(t("flags.override_invalid_user_id"));
      return;
    }
    put.mutate(
      { user_id: parsed, enabled: true },
      {
        onSuccess: () => {
          setUserId("");
          setError(null);
        },
        onError: (err) => setError((err as Error).message),
      }
    );
  }

  return (
    <div style={COL}>
      <div style={{ ...LABEL, fontWeight: 600, fontSize: 13 }}>{t("flags.overrides")}</div>
      {isLoading && <div>{t("common.loading")}</div>}
      {(data ?? []).map((row) => (
        <div
          key={row.user_id}
          style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12 }}
        >
          <span style={LABEL}>{row.tg_id ?? "—"}</span>
          <span style={LABEL}>{row.username ?? "—"}</span>
          <label style={CHECK_LABEL}>
            <input
              type="checkbox"
              aria-label={`override-${row.user_id}-enabled`}
              checked={row.enabled}
              onChange={(e) => put.mutate({ user_id: row.user_id, enabled: e.target.checked })}
            />
          </label>
          <button style={LINK_BTN} onClick={() => del.mutate(row.user_id)}>
            {t("common.remove")}
          </button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          style={FIELD}
          placeholder={t("flags.user_id_placeholder")}
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
        />
        <button style={BTN} onClick={addOverride}>
          {t("flags.add_override")}
        </button>
      </div>
      {error && <div style={ERROR}>{error}</div>}
    </div>
  );
}
